import React, { useId, useState } from 'react';

type TooltipPosition =
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right';

interface HoverTooltipProps {
    /** The tooltip text/content shown on hover. */
    content: React.ReactNode;
    /** The element the tooltip is attached to (icon, badge, button, etc.). */
    children: React.ReactNode;
    /** Where the tooltip appears relative to the children. Defaults to "top". */
    position?: TooltipPosition;
    /** Text alignment inside the tooltip. Use "left" for rich/multi-item content. Defaults to "center". */
    align?: 'left' | 'center';
    /** Tailwind width class for the tooltip box. Defaults to fit-content with a max width. */
    width?: string;
    /** Extra classes applied to the wrapper element. */
    className?: string;
    /** Accessible label for the wrapper (falls back to the content when it's a string). */
    ariaLabel?: string;
    /** ARIA role for the wrapper (e.g. "img" for an icon-only trigger). */
    role?: string;
    /**
     * Whether the wrapper itself should be keyboard-focusable. Defaults to true so
     * non-interactive triggers (icons, badges, text) are reachable by keyboard. Set
     * to false when the child is already focusable (e.g. a button) to avoid a
     * duplicate tab stop — the tooltip still opens on the child's focus.
     */
    focusable?: boolean;
    /** Optional test id for the wrapper. */
    dataTestId?: string;
}

const positionClasses: Record<TooltipPosition, string> = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-1',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-1',
    left: 'right-full top-1/2 -translate-y-1/2 mr-1',
    right: 'left-full top-1/2 -translate-y-1/2 ml-1',
    'top-left': 'bottom-full left-0 mb-1',
    'top-right': 'bottom-full right-0 mb-1',
    'bottom-left': 'top-full left-0 mt-1',
    'bottom-right': 'top-full right-0 mt-1',
};

/**
 * Standard hover tooltip used across the portal. Follows the ARIA tooltip pattern:
 * shows on hover AND keyboard focus, is dismissible with Escape, uses the shared
 * white / border-gray-lines / shadow style (no native `title` delay), and wires the
 * trigger to the tooltip via aria-describedby (cloned onto the child element so a
 * focused control is correctly associated). Wrap any element to give it a tooltip.
 */
export const HoverTooltip: React.FC<HoverTooltipProps> = ({
    content,
    children,
    position = 'top',
    align = 'center',
    width = 'w-max max-w-[220px]',
    className = '',
    ariaLabel,
    role,
    focusable = true,
    dataTestId,
}) => {
    const tooltipId = useId();
    const [open, setOpen] = useState(false);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') setOpen(false);
    };

    // Merge aria-describedby onto the actual child so a focused control (e.g. a
    // button) is associated with the tooltip, not just the wrapper.
    const trigger = React.isValidElement(children)
        ? React.cloneElement(children as React.ReactElement<any>, {
            'aria-describedby': [(children as React.ReactElement<any>).props['aria-describedby'], tooltipId]
                .filter(Boolean)
                .join(' '),
        })
        : children;

    return (
        <span
            className={`relative inline-flex ${className}`}
            role={role}
            aria-label={ariaLabel ?? (typeof content === 'string' ? content : undefined)}
            aria-describedby={tooltipId}
            tabIndex={focusable ? 0 : undefined}
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            onKeyDown={handleKeyDown}
            data-testid={dataTestId}
        >
            {trigger}
            <div
                id={tooltipId}
                role="tooltip"
                className={`${open ? 'block' : 'hidden'} absolute ${positionClasses[position]} ${width} ${align === 'left' ? 'text-left' : 'text-center'} bg-white border border-gray-lines p-2 rounded shadow-lg z-10 text-secondary-gray text-sm font-normal pointer-events-none`}
            >
                {content}
            </div>
        </span>
    );
};
